import React, { useState } from 'react';

const ReferralSection = ({ referralCode, referralCount, referralEarnings }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard?.writeText(referralCode || 'JAMES2024')?.catch(() => {});
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const tiers = [
    { count: 1, reward: 500, label: '1st Driver', achieved: true },
    { count: 3, reward: 1500, label: '3 Drivers', achieved: true },
    { count: 5, reward: 3000, label: '5 Drivers', achieved: false },
    { count: 10, reward: 7500, label: '10 Drivers', achieved: false },
  ];

  const currentCount = referralCount || 2;

  return (
    <div className="rounded-2xl bg-[#0f2040] border border-white/8 overflow-hidden">
      {/* Header */}
      <div className="p-4 border-b border-white/8">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-white font-bold text-sm">Refer & Earn</h3>
            <p className="text-white/40 text-xs mt-0.5">Earn KSh 500 per driver you recruit</p>
          </div>
          <div className="w-10 h-10 rounded-xl bg-[#ffd700]/10 border border-[#ffd700]/20 flex items-center justify-center">
            <svg viewBox="0 0 24 24" fill="#ffd700" className="w-5 h-5">
              <path d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z"/>
            </svg>
          </div>
        </div>
      </div>
      <div className="p-4 space-y-4">
        {/* Stats */}
        <div className="grid grid-cols-2 gap-2">
          <div className="bg-white/5 rounded-xl p-3 text-center">
            <p className="text-[#ffd700] font-black text-xl">{currentCount}</p>
            <p className="text-white/40 text-xs">Drivers Referred</p>
          </div>
          <div className="bg-white/5 rounded-xl p-3 text-center">
            <p className="text-[#ffd700] font-black text-xl">KSh {(referralEarnings || 1000)?.toLocaleString()}</p>
            <p className="text-white/40 text-xs">Total Earned</p>
          </div>
        </div>

        {/* Referral code */}
        <div>
          <p className="text-white/40 text-xs mb-2 uppercase tracking-wider">Your Referral Code</p>
          <div className="flex gap-2">
            <div className="flex-1 bg-white/5 rounded-xl px-4 py-3 border border-[#ffd700]/20 flex items-center">
              <span className="text-[#ffd700] font-black text-lg tracking-widest">{referralCode || 'JAMES2024'}</span>
            </div>
            <button
              onClick={handleCopy}
              className={`px-4 rounded-xl font-semibold text-sm transition-all active:scale-95 ${
                copied
                  ? 'bg-green-500 text-white' :'bg-[#ffd700] text-[#0a1628] hover:bg-[#f0c800]'
              }`}
            >
              {copied ? '✓ Copied' : 'Copy'}
            </button>
          </div>
        </div>

        {/* Tier progress */}
        <div>
          <p className="text-white/40 text-xs mb-2 uppercase tracking-wider">Reward Tiers</p>
          <div className="space-y-2">
            {tiers?.map(tier => (
              <div key={tier?.count} className={`flex items-center gap-3 p-2.5 rounded-xl transition-all ${
                tier?.achieved ? 'bg-[#ffd700]/10 border border-[#ffd700]/20' : 'bg-white/3 border border-white/5'
              }`}>
                <div className={`w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 ${
                  tier?.achieved ? 'bg-[#ffd700]' : currentCount >= tier?.count - 1 ? 'bg-white/10' : 'bg-white/5'
                }`}>
                  {tier?.achieved ? (
                    <svg viewBox="0 0 24 24" fill="#0a1628" className="w-4 h-4">
                      <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
                    </svg>
                  ) : (
                    <span className="text-white/30 text-xs font-bold">{tier?.count}</span>
                  )}
                </div>
                <div className="flex-1">
                  <p className={`text-xs font-semibold ${tier?.achieved ? 'text-white' : 'text-white/40'}`}>{tier?.label}</p>
                </div>
                <span className={`text-xs font-bold ${tier?.achieved ? 'text-[#ffd700]' : 'text-white/30'}`}>
                  KSh {tier?.reward?.toLocaleString()}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Share button */}
        <button className="w-full py-3 rounded-xl bg-[#ffd700]/10 border border-[#ffd700]/20 text-[#ffd700] font-semibold text-sm hover:bg-[#ffd700]/15 transition-all active:scale-95 flex items-center justify-center gap-2">
          <svg viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4">
            <path d="M18 16.08c-.76 0-1.44.3-1.96.77L8.91 12.7c.05-.23.09-.46.09-.7s-.04-.47-.09-.7l7.05-4.11c.54.5 1.25.81 2.04.81 1.66 0 3-1.34 3-3s-1.34-3-3-3-3 1.34-3 3c0 .24.04.47.09.7L8.04 9.81C7.5 9.31 6.79 9 6 9c-1.66 0-3 1.34-3 3s1.34 3 3 3c.79 0 1.5-.31 2.04-.81l7.12 4.16c-.05.21-.08.43-.08.65 0 1.61 1.31 2.92 2.92 2.92 1.61 0 2.92-1.31 2.92-2.92s-1.31-2.92-2.92-2.92z"/>
          </svg>
          Share Referral Link
        </button>
      </div>
    </div>
  );
};

export default ReferralSection;
